import { CollectionsMap } from 'constants/collections'
import { Currencies } from 'constants/currencies'
import { NumericMenusMap, RefinementListsMap } from 'constants/instantsearch'
import { useCurrencies } from 'contexts'
import { useCallback, useMemo } from 'react'

export const ArtworksCategories = {
  findsUnder1000: 'finds_under_1000',
  midPricedArt: 'mid_priced_art',
  rareLuxuryArt: 'rare_luxury_art',
  prints: 'prints',
  sculptures: 'sculptures',
  collectibles: 'collectibles',
  photography: 'photography',
  original: 'original',
  books: 'books',
  small: 'small',
  medium: 'medium',
  large: 'large',
  european: 'european',
  american: 'american',
  african: 'african',
  asian: 'asian',
} as const
export type ArtworksCategory =
  (typeof ArtworksCategories)[keyof typeof ArtworksCategories]

/**
 * There are some recommendations displayed in the search bar for artworks. This hook centralizes the filters corresponding to each of them.
 */
export function useArtworksCategories() {
  const { getConvertedValue } = useCurrencies()

  const convert = useCallback(
    (value: number) => {
      return getConvertedValue(value, {
        from: Currencies.USER,
        to: Currencies.GBP,
      })
    },
    [getConvertedValue]
  )

  const items = useMemo(() => {
    const map = {
      [ArtworksCategories.findsUnder1000]: {
        initialFilterState: {
          numericMenu: {
            [NumericMenusMap.lowestAsk.attribute]: `${convert(2000)}:${convert(
              100000
            )}`,
          },
        },
      },

      [ArtworksCategories.midPricedArt]: {
        initialFilterState: {
          numericMenu: {
            [NumericMenusMap.lowestAsk.attribute]: `${convert(
              100000
            )}:${convert(500000)}`,
          },
        },
      },

      [ArtworksCategories.rareLuxuryArt]: {
        initialFilterState: {
          numericMenu: {
            [NumericMenusMap.lowestAsk.attribute]: `${convert(1000000)}:`,
          },
        },
      },

      [ArtworksCategories.small]: {
        initialFilterState: {
          refinementList: {
            [RefinementListsMap.size.attribute]: ['Small'] as string[],
          },
        },
      },
      [ArtworksCategories.medium]: {
        initialFilterState: {
          refinementList: {
            [RefinementListsMap.size.attribute]: ['Medium'] as string[],
          },
        },
      },
      [ArtworksCategories.large]: {
        initialFilterState: {
          refinementList: {
            [RefinementListsMap.size.attribute]: ['Large'] as string[],
          },
        },
      },

      [ArtworksCategories.european]: CollectionsMap.european_artists,
      [ArtworksCategories.american]: CollectionsMap.american_artists,
      [ArtworksCategories.african]: CollectionsMap.african_artists,
      [ArtworksCategories.asian]: CollectionsMap.asian_artists,
      [ArtworksCategories.prints]: CollectionsMap.prints,
      [ArtworksCategories.sculptures]: CollectionsMap.sculptures,
      [ArtworksCategories.collectibles]: CollectionsMap.collectibles,
      [ArtworksCategories.photography]: CollectionsMap.photography,
      [ArtworksCategories.books]: CollectionsMap.art_books,
      [ArtworksCategories.original]: CollectionsMap.original,
    }

    return map
  }, [convert])

  return items
}
